import React, { ReactNode } from 'react';
import {
  Radio,
  FormControl,
  FormControlProps,
  FormControlLabel,
  FormHelperText,
  RadioGroup,
  FormLabel,
  Tooltip,
} from '@mui/material';
import { Choice } from '../../../../../types/utility';
import { Control, Controller, FieldValues } from 'react-hook-form';

type RadioGroupFieldProps<T extends FieldValues = any> = {
  control?: Control<T>;
  name: string;
  choices: Choice[];
  label: ReactNode;
  contentAfterLabel?: ReactNode;
  readOnly?: boolean;
  touched?: boolean;
  tooltip?: string;
} & FormControlProps;

export default function RadioGroupFields(props: RadioGroupFieldProps) {
  const {
    name,
    control,
    label,
    contentAfterLabel,
    choices,
    readOnly,
    touched: defaultTouched,
    tooltip,
    ...rest
  } = props;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const { isTouched: fieldTouched, error } = fieldState;
        const touched = defaultTouched || fieldTouched;
        const { onChange, onBlur, value, ref, name } = field;

        const showError = touched && error ? true : false;
        function _renderHelperText() {
          if (showError) {
            return (
              <FormHelperText error sx={{ mx: 0 }}>
                {error?.message}
              </FormHelperText>
            );
          }
        }
        return (
          <FormControl {...rest} error={showError}>
            <FormLabel id={name}>{label}</FormLabel>
            {contentAfterLabel}
            <Tooltip title={tooltip}>
              <RadioGroup
                aria-labelledby={name}
                name={name}
                value={value}
                onChange={onChange}
                ref={ref}
                onFocus={(e) => {
                  rest.onFocus?.(e); // Preserve additional onFocus from `...rest`
                  (field as any).onFocus?.(e); // Ensure RHF validation runs (Need to declare it as unknown to avoid type error. Typescript doesn't know that field is a Controller field which in fact has onFocus)
                }}
                onBlur={(e) => {
                  rest.onBlur?.(e); // Preserve additional onBlur from `...rest`
                  (field as any).onBlur?.(e); // Ensure RHF validation runs (Need to declare it as unknown to avoid type error. Typescript doesn't know that field is a Controller field which in fact has onBlur)
                }}
              >
                {choices.map((choice, i) => (
                  <FormControlLabel
                    key={i}
                    value={choice.value}
                    control={<Radio />}
                    label={choice.label}
                    checked={choice.value === value}
                  />
                ))}
              </RadioGroup>
            </Tooltip>
            {_renderHelperText()}
          </FormControl>
        );
      }}
    />
  );
}
